import { useCallback, useEffect, useState } from 'react';
import paymentLog from '../../services/helpers/PaymentLogger';
import {
  CUSTOMER_SESSION_REFRESH_MARGIN_MS,
  CustomerSessionAnswer,
  StripeCustomerSessionCache,
  customerSessionKey,
} from '../../services/helpers/StripeCustomerSessionCache';

export { CUSTOMER_SESSION_REFRESH_MARGIN_MS };

type Answer = CustomerSessionAnswer & { request: string };

/** The secret the Elements were created with; `undefined` until the first answer is in. */
type Created = { generation: number; clientSecret: undefined | null | string };

export interface StripeCustomerSession {
  /** An answer is in, with a secret or without one: the Elements can be created. */
  ready: boolean;
  /** What the Elements are created with: null for a guest, a failed request, or a dropped session. */
  clientSecret: null | string;
  /**
   * Moves whenever the Elements have to be created again — a customer logging in or out, a secret
   * refreshed after it expired, a session dropped after it failed to load — and never for the first
   * answer, so a logged-in checkout mounts its Elements once.
   */
  generation: number;
  /** The Elements could not load with the secret: carry on without it. */
  drop: () => void;
}

/**
 * The CustomerSession that lets the Payment Element draw Stripe's own "save payment details" box under
 * the card row, for a logged-in customer of a restaurant on the Stripe Elements checkout. What the
 * customer ticks travels on the ConfirmationToken, and the backend confirms the intent with it.
 *
 * The Elements take `customerSessionClientSecret` at creation only, so the coordinator holds them
 * back until the first answer is in and keys them on `generation`. The checkout step asks ahead
 * (StripeCustomerSessionCache.prefetch), so on the payment screen the answer is usually in on the
 * first render and nothing waits. A failed request answers without a secret, and so does a session
 * the Elements could not load with (`drop`): the box is optional and must never take the checkout
 * down. A secret Stripe has expired is asked for again when the customer comes back to the page,
 * unless a payment is in flight (`holdRefresh`).
 */
export default function useStripeCustomerSession(
  enabled: boolean,
  companyId: null | number | undefined,
  userId: null | number | undefined,
  { holdRefresh = false }: { holdRefresh?: boolean } = {},
): StripeCustomerSession {
  // Per customer, so a customer who logs in on a page another one logged out of never gets theirs.
  const key = enabled && companyId ? `${companyId}:${userId ?? ''}` : null;
  const cacheKey = enabled ? customerSessionKey(companyId, userId) : null;
  const [refreshes, setRefreshes] = useState(0);
  const request = key === null ? null : `${key}:${refreshes}`;
  const [fetched, setFetched] = useState<Answer | null>(null);
  const [dropped, setDropped] = useState<null | string>(null);
  const [created, setCreated] = useState<Created>({ generation: 0, clientSecret: undefined });

  // A secret the checkout step already asked for is read during render, so the Elements are created
  // on the payment screen's first render rather than one round trip later.
  const kept = request !== null && fetched?.request !== request ? StripeCustomerSessionCache.peek(cacheKey) : undefined;
  const answer: Answer | null = fetched?.request === request ? fetched : kept ? { request, ...kept } : null;

  useEffect(() => {
    if (request === null) {
      return;
    }

    let cancelled = false;

    StripeCustomerSessionCache.fetch(cacheKey, companyId).then((result) => {
      if (!cancelled) {
        setFetched({ request, ...result });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [request]);

  // The secret the current answer gives the Elements; `undefined` while that answer is on its way.
  const answeredSecret: undefined | null | string =
    request === null
      ? null
      : answer === null
        ? undefined
        : answer.clientSecret === dropped
          ? null
          : answer.clientSecret;

  // While a new answer is on its way (a login, a refresh) the Elements already created keep working.
  // Adjusted during render, as React has it for state derived from earlier renders: the Elements are
  // keyed on it, so it must change in the same render the new secret appears in.
  let current = created;

  if (answeredSecret !== undefined && answeredSecret !== created.clientSecret) {
    current = {
      generation: created.clientSecret === undefined ? created.generation : created.generation + 1,
      clientSecret: answeredSecret,
    };
    setCreated(current);
  }

  const inUse = current.clientSecret ?? null;
  const expiresAtMs =
    inUse !== null && answer?.clientSecret === inUse && answer.expiresAt ? answer.expiresAt * 1000 : null;

  useEffect(() => {
    if (expiresAtMs === null || holdRefresh) {
      return;
    }

    const refreshIfExpired = () => {
      if (document.visibilityState === 'visible' && Date.now() >= expiresAtMs - CUSTOMER_SESSION_REFRESH_MARGIN_MS) {
        paymentLog.info('stripe', 'customer_session_expired', { company_id: companyId });
        setRefreshes((count) => count + 1);
      }
    };

    window.addEventListener('focus', refreshIfExpired);
    document.addEventListener('visibilitychange', refreshIfExpired);

    return () => {
      window.removeEventListener('focus', refreshIfExpired);
      document.removeEventListener('visibilitychange', refreshIfExpired);
    };
  }, [expiresAtMs, holdRefresh]);

  const drop = useCallback(() => {
    if (inUse === null) {
      return;
    }

    paymentLog.warn('stripe', 'customer_session_dropped', { company_id: companyId });
    StripeCustomerSessionCache.forget(cacheKey, inUse);
    setDropped(inUse);
  }, [inUse, companyId, cacheKey]);

  return {
    ready: current.clientSecret !== undefined,
    clientSecret: inUse,
    generation: current.generation,
    drop,
  };
}
